/* eslint-disable max-statements */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Label } from '@nimbus-ds/components';
import { SearchIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { Domain } from '@tiendanube/common/src/enums';
import { Stack } from '@tiendanube/components';
import ModalAside from 'App/components/lab/ModalAside';
import { FEATURE_METAFIELDS_ORDERS, FEATURE_SEARCH_FILTER } from 'App/features';
import { B2B_WHOLESALE } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { DateFilter } from 'commons/components';
import { optionValue } from 'commons/components/DateFilter/DateFilter';
import MultiSelect from 'commons/components/MultiSelect';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import SearchMultiSelect from 'commons/components/SearchMultiSelect';
import { useBoolean } from 'commons/hooks';
import {
  ORDERS_METAFIELD_FILTERS_DROPDOWN,
  ORDERS_SHOW_FILTERS,
} from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { UpsellResultEnum } from 'domains/Billing/UpsellFlow/hooks/useUpsellFlow';
import { MetafieldSelectedInterface } from 'domains/Metafields/types';
import {
  OrderPaymentMethodsFilter,
  OrderApplicationsFilter,
} from 'domains/Orders/components';
import OrderPaymentProvidersFilter from 'domains/Orders/components/OrderPaymentProvidersFilter';
import { ProductInterface } from 'domains/Orders/components/ProductSearcher';
import ProductsFilter from 'domains/Orders/components/ProductsFilter';
import { ProductsFilterValue } from 'domains/Orders/components/ProductsFilter/ProductsFilter';
import {
  ProductUnitsCountFilter,
  ProductUnitsCountFilterValue,
} from 'domains/Orders/components/ProductUnitsCountFilter/ProductUnitsCountFilter';
import { useProductUnitsCountFilter } from 'domains/Orders/components/ProductUnitsCountFilter/useProductUnitsCountFilter';
import { CreateSavedSearchModal } from 'domains/Orders/components/SavedSearches';
import { METAFIELD_FILTER_PREFIX } from 'domains/Orders/constants';
import useGetOrdersFilters from 'domains/Orders/Orders/hooks/useGetOrdersFilters/useGetOrdersFilters';
import useIsProductsFilterEnabled from 'domains/Orders/Orders/hooks/useIsProductsFilterEnabled';
import { useIsProductUnitsCountFilterEnabled } from 'domains/Orders/Orders/hooks/useIsProductUnitsCountFilterEnabled';
import useOrderCouponSearchOptions from 'domains/Orders/Orders/hooks/useOrderCouponSearchOptions/useOrderCouponSearchOptions';
import { useSavedSearchesUpsell } from 'domains/Orders/Orders/hooks/useSavedSearchesUpsell';
import {
  FilterStatusEnum,
  FiltersParamsType,
  ProductCountOptionsEnum,
  defaultFilters,
  PIPE_SEPARATOR,
  COMMA_SEPARATOR,
  paymentProviderFilter,
  paymentMethodsFilter,
  paymentStatusFilter,
  fulfillmentStatusFilter,
  locationFilter,
  shippingMethodFilter,
  statusFilter,
  couponIdsRawFilter,
  originFilter,
} from 'domains/Orders/Orders/ordersService';
import { areFiltersEquals } from 'domains/Orders/Orders/utils/OrderListFilter/utils';
import {
  trackingOrderFilter,
  trackingOrderMetafieldFilter,
  trackingOrdersSearchFilterClick,
} from 'domains/Orders/tracking';
import useLocations from 'domains/Shipping/Locations/hooks/useLocations/useLocations';
import { useHasMetaplans } from 'domains/Subscriptions';
import { MetafieldsOrdersFilters } from './components';
import { OrdersSubscriptionFilter } from './components/OrdersSubscriptionFilter';
import { useOrdersSubscriptionFilter } from './components/OrdersSubscriptionFilter/useOrdersSubscriptionFilter';
import { OrdersWholesaleFilter } from './components/OrdersWholesaleFilter';
import { useOrdersWholesaleFilter } from './components/OrdersWholesaleFilter/useOrdersWholesaleFilter';
import { SavedSearchName } from './components/SavedSearchName';
import { FilterModalFooter } from './FilterModalFooter';
import { useFilterOptionsSelects } from './hooks';

interface FilterModalProps {
  show: boolean;
  filter: FiltersParamsType;
  selectedProducts: ProductInterface<true>[];
  productUnitsCountFilterValue: ProductUnitsCountFilterValue;
  addSelectedProductParam: (products: ProductInterface<true>[]) => void;
  serializeProducts: (products: ProductInterface<true>[]) => string;
  hideModal: () => void;
  onCancel?: () => void;
  addParam: (filters: FiltersParamsType) => void;
  onConfirmSavedSearch?: (name: string, filters: FiltersParamsType) => void;
  addProductUnitsCountFilterParam: (
    value: ProductUnitsCountFilterValue,
  ) => void;
  onCouponOptionsLabel: (options: OptionType[]) => void;
  savedSearchModalMode?: 'create' | 'edit';
  savedSearchName?: string;
}

function FilterModal({
  show,
  hideModal,
  filter,
  addParam,
  selectedProducts,
  addSelectedProductParam,
  serializeProducts,
  productUnitsCountFilterValue,
  addProductUnitsCountFilterParam,
  onCancel,
  onCouponOptionsLabel,
  onConfirmSavedSearch,
  savedSearchModalMode,
  savedSearchName,
}: FilterModalProps): JSX.Element {
  const { t } = useTranslation([Domain.ORDERS]);
  const { paymentMethodOptions, paymentProviderOptions, applicationOptions } =
    useGetOrdersFilters();
  const {
    isProductsCountFilterValid,
    getProductsCountFilterOption,
    sanitizeProductUnitsCountFilter,
  } = useProductUnitsCountFilter();
  const { locations } = useLocations();
  const hasProductUnitsCountFilter = useIsProductUnitsCountFilterEnabled();

  const [values, setValues] = useState(filter);

  const {
    defaultStatus,
    statusOptionsForMultiSelect,
    defaultShippingMethod,
    shippingMethodOptionsForMultiSelect,
    defaultLocation,
    locationOptionsForMultiSelect,
    defaultFulfillmentStatus,
    fulfillmentStatusOptions,
    defaultPaymentStatus,
    paymentOptions,
    defaultPaymentMethod,
    paymentMethodOptionsForMultiSelect,
    defaultPaymentProvider,
    paymentProviderOptionsForMultiSelect,
    defaultOrigin,
    originOptionsForMultiSelect,
  } = useFilterOptionsSelects({
    values,
    locations,
    paymentMethodOptions,
    paymentProviderOptions,
  });
  const [selectedProductsValues, setSelectedProductsValues] =
    useState(selectedProducts);
  const [selectedOption, setSelectedOption] = useState(optionValue.ALL);
  const [selectedProductUnitsCountValue, setSelectedProductUnitsCountValue] =
    useState<ProductUnitsCountFilterValue>(productUnitsCountFilterValue);
  const [hasProductsCountFilterError, setHasProductsCountFilterError] =
    useState(false);
  const {
    options: couponOptions,
    onInputChange,
    getDefaultCouponValues,
    loading,
  } = useOrderCouponSearchOptions();
  const hasProductsFilter = useIsProductsFilterEnabled();
  const { getSubscriptionFilterOptionTracking } = useOrdersSubscriptionFilter();
  const { getWholesaleFilterOptionTracking } = useOrdersWholesaleFilter();
  const [hasFilterWholesaleTag] = useHasTags(B2B_WHOLESALE);

  const isLocationsFilterShowable = locations.length > 1;
  const [defaultCouponOptions, setDefaultCouponOptions] = useState<
    OptionType[]
  >([]);
  const hasInitializedCoupons = useRef(false);
  const { hasMetaplans, fetchHasMetaplans } = useHasMetaplans();

  const [savedSearchNameValue, setSavedSearchNameValue] = useState(
    savedSearchName ?? '',
  );
  const [showSubscriptionFilter, setShowSubscriptionFilter] = useState(
    hasMetaplans ?? false,
  );

  useEffect(() => {
    setSavedSearchNameValue(savedSearchName ?? '');
  }, [savedSearchName]);

  useEffect(() => {
    fetchHasMetaplans();
  }, [fetchHasMetaplans]);

  useEffect(() => {
    setShowSubscriptionFilter(hasMetaplans ?? false);
  }, [hasMetaplans]);

  const disabledCreateSavedSearchOption = useMemo(
    () => areFiltersEquals(values, defaultFilters),
    [values],
  );
  const [isSavedSearchModalOpen, openSavedSearchModal, closeSavedSearchModal] =
    useBoolean(false);
  // Need this to not show the side modal and the saved search modal at the same time
  const [
    isSavedSearchUpsellModalOpen,
    openSavedSearchUpsellModal,
    closeSavedSearchUpsellModal,
  ] = useBoolean(false);
  const openSavedSearchModalWithUpsell = useSavedSearchesUpsell(() => {
    openSavedSearchModal();
    closeSavedSearchUpsellModal();
  }, closeSavedSearchUpsellModal);
  const handleOpenSavedSearchModal = () => {
    openSavedSearchModalWithUpsell();
    openSavedSearchUpsellModal();
  };

  useEffect(() => {
    const resolveMissingCouponNames = async () => {
      const resolved = await getDefaultCouponValues(values.couponIdsRaw);
      setDefaultCouponOptions(resolved);
      if (!hasInitializedCoupons.current) {
        onCouponOptionsLabel(resolved);
        hasInitializedCoupons.current = true;
      }
    };

    if (values.couponIdsRaw) {
      resolveMissingCouponNames();
    } else {
      setDefaultCouponOptions([]);
    }
  }, [values.couponIdsRaw, onCouponOptionsLabel, getDefaultCouponValues]);

  useEffect(() => {
    setValues(filter);
  }, [filter]);

  useEffect(() => {
    setSelectedProductsValues(selectedProducts);
  }, [selectedProducts]);

  useEffect(() => {
    setSelectedProductUnitsCountValue(productUnitsCountFilterValue);
  }, [productUnitsCountFilterValue]);

  const handleChange = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    includeCallbackResult: true,
    callback: (upsellResult: UpsellResultEnum, { name, value }) => {
      setValues((prev) => ({ ...prev, [name]: value }));
      trackingOrdersSearchFilterClick(upsellResult, 'filters', name);
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'filters');
      }
    },
  });

  const handleMultiSelectChange = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    includeCallbackResult: true,
    callback: (upsellResult: UpsellResultEnum, name, selectedValues) => {
      let fieldValue: string;

      if (name === statusFilter) {
        fieldValue =
          selectedValues.length === 0
            ? FilterStatusEnum.ALL
            : selectedValues.map(({ value }) => value).join(',');
      } else {
        // Use pipe separator only for payment provider to avoid conflicts with commas in provider IDs
        const separator =
          name === paymentProviderFilter ? PIPE_SEPARATOR : COMMA_SEPARATOR;
        fieldValue = selectedValues.map(({ value }) => value).join(separator);
      }

      setValues((prev) => ({
        ...prev,
        [name]: fieldValue,
      }));
      trackingOrdersSearchFilterClick(upsellResult, 'filters', name);
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'filters');
      }
    },
  });

  const handleProductsFilterChange = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    includeCallbackResult: true,
    callback: (upsellResult: UpsellResultEnum, value: ProductsFilterValue) => {
      setSelectedProductsValues(value.products);
      const serializedProducts = serializeProducts(value.products);
      const newValues = {
        products: serializedProducts,
        exactProductSet: value.exactProductSet,
      };
      setValues((prev) => ({ ...prev, ...newValues }));
      trackingOrdersSearchFilterClick(upsellResult, 'filters', 'products');
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'filters', 'products');
      }
    },
  });

  const handleProductsCountFilterChange = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    includeCallbackResult: true,
    callback: (
      upsellResult: UpsellResultEnum,
      value: ProductUnitsCountFilterValue,
    ) => {
      const newValue = {
        minUnits: value.minUnits,
        maxUnits: value.maxUnits,
      };
      setSelectedProductUnitsCountValue({
        selectedOption: value.selectedOption,
        ...newValue,
      });
      setHasProductsCountFilterError(false);
      setValues((prev) => ({ ...prev, ...newValue }));
      trackingOrdersSearchFilterClick(
        upsellResult,
        'filters',
        'products_count',
      );
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(
          upsellResult,
          'filters',
          'products_count',
        );
      }
    },
  });

  const handleDateRangeChange = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    includeCallbackResult: true,
    callback: (
      upsellResult: UpsellResultEnum,
      { option, dateFrom, dateTo },
    ) => {
      setValues((prev) => ({ ...prev, dateFrom: dateFrom, dateTo: dateTo }));
      setSelectedOption(option);
      trackingOrdersSearchFilterClick(upsellResult, 'filters', 'date_range');
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'filters', 'date_range');
      }
    },
  });

  const handleClear = () => {
    setValues({
      status: FilterStatusEnum.EMPTY,
      dateFrom: '',
      dateTo: '',
      fulfillmentStatus: FilterStatusEnum.EMPTY,
      paymentStatus: FilterStatusEnum.EMPTY,
      paymentMethods: '',
      shippingMethod: '',
      paymentProvider: '',
      location: '',
      origin: '',
      appId: '',
      products: '',
      exactProductSet: '',
      minUnits: '',
      maxUnits: '',
      isWholesale: '',
      isSubscription: '',
    });
    setSelectedProductUnitsCountValue({
      selectedOption: ProductCountOptionsEnum.ALL,
      minUnits: '',
      maxUnits: '',
    });
    setSelectedProductsValues([]);
    setSavedSearchNameValue('');
    onCancel?.();
  };

  const handleConfirm = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    includeCallbackResult: true,
    callback: (upsellResult: UpsellResultEnum) => {
      const errors = !isProductsCountFilterValid(values);
      setHasProductsCountFilterError(errors);
      if (errors) return;
      addSelectedProductParam(selectedProductsValues);
      const unitsFilterValue = sanitizeProductUnitsCountFilter(
        selectedProductUnitsCountValue.selectedOption,
        values.minUnits,
        values.maxUnits,
      );
      setSelectedProductUnitsCountValue(unitsFilterValue);
      addProductUnitsCountFilterParam(unitsFilterValue);
      addParam({
        ...values,
        minUnits: unitsFilterValue.minUnits,
        maxUnits: unitsFilterValue.maxUnits,
      });
      if (savedSearchModalMode) {
        onConfirmSavedSearch?.(savedSearchNameValue, {
          ...values,
          minUnits: unitsFilterValue.minUnits,
          maxUnits: unitsFilterValue.maxUnits,
        });
      }
      onCouponOptionsLabel(defaultCouponOptions);
      hideModal();
      const shippingStatusFilterValues = values.fulfillmentStatus.split(',');
      const paymentStatusFilterValues = values.paymentStatus.split(',');
      const isSubscriptionFilterValues = getSubscriptionFilterOptionTracking(
        values.isSubscription,
      );
      const isWholesaleFilterValues = getWholesaleFilterOptionTracking(
        values.isWholesale,
      );
      setSavedSearchNameValue('');
      closeSavedSearchModal();
      trackingOrderFilter({
        orderStatusFilter: values.status,
        dateRangeFilter: selectedOption,
        shippingStatusFilter: shippingStatusFilterValues,
        shippingStatusFilterMultiselect: String(
          shippingStatusFilterValues.length > 1,
        ),
        paymentStatusFilter: paymentStatusFilterValues,
        paymentStatusFilterMultiselect: String(
          paymentStatusFilterValues.length > 1,
        ),
        paymentMethodFilter: String(!!values.paymentMethods),
        paymentProviderFilter: String(!!values.paymentProvider),
        shippingFilter: String(!!values.shippingMethod),
        channelFilter: String(!!values.origin),
        applicationFilter: String(!!values.appId),
        productsFilter: String(!!values.products),
        countSelectedProductsFilter: String(selectedProductsValues.length),
        exactProductSetValue: String(values.exactProductSet),
        productUnitsCountFilter: getProductsCountFilterOption(
          values.minUnits,
          values.maxUnits,
        ),
        minUnitsFilter: String(values.minUnits),
        maxUnitsFilter: String(values.maxUnits),
        couponIdsFilter: String(!!values.couponIdsRaw),
        isWholesaleFilter: String(isWholesaleFilterValues),
        isSubscriptionFilter: String(isSubscriptionFilterValues),
      });
      trackingOrdersSearchFilterClick(upsellResult, 'filters', 'modal_confirm');
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(
          upsellResult,
          'filters',
          'modal_confirm',
        );
      }
    },
  });

  const handleChangeMetafields = (id: string, value: string) => {
    trackingOrderMetafieldFilter(id, value);
    if (value !== null) {
      setValues((values) => ({
        ...values,
        [METAFIELD_FILTER_PREFIX + id]: value,
      }));
      return;
    }
    setValues((values) => {
      const newValues = { ...values };
      delete newValues[METAFIELD_FILTER_PREFIX + id];
      return newValues;
    });
  };

  const onChangeMetafieldFilter = useUpsellFlow({
    title: t('filter.upsell.metafieldFilters'),
    featureKey: FEATURE_METAFIELDS_ORDERS,
    trackingSource: ORDERS_METAFIELD_FILTERS_DROPDOWN,
    includeCallbackResult: true,
    callback: (upsellResult: UpsellResultEnum, id: string, value: string) => {
      handleChangeMetafields(id, value);
      trackingOrdersSearchFilterClick(upsellResult, 'filters', 'metafields');
    },
    asAside: true,
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'filters', 'metafields');
      }
    },
  });

  const selecteds = Object.entries(values)
    .filter(([key]) => key.startsWith(METAFIELD_FILTER_PREFIX))
    .map(([key, value]) => ({
      id: key.split(METAFIELD_FILTER_PREFIX)[1],
      value,
    })) as MetafieldSelectedInterface[];

  const handleStatusChange = useCallback(
    (selected) => handleMultiSelectChange(statusFilter, selected),
    [handleMultiSelectChange],
  );

  const handlePaymentStatusChange = useCallback(
    (selected) => handleMultiSelectChange(paymentStatusFilter, selected),
    [handleMultiSelectChange],
  );

  const handleFulfillmentStatusChange = useCallback(
    (selected) => handleMultiSelectChange(fulfillmentStatusFilter, selected),
    [handleMultiSelectChange],
  );

  const handleShippingMethodChange = useCallback(
    (selected) => handleMultiSelectChange(shippingMethodFilter, selected),
    [handleMultiSelectChange],
  );

  const handleCouponChange = useCallback(
    (selected) => handleMultiSelectChange(couponIdsRawFilter, selected),
    [handleMultiSelectChange],
  );

  const handleLocationChange = useCallback(
    (selected) => handleMultiSelectChange(locationFilter, selected),
    [handleMultiSelectChange],
  );
  const handleOriginChange = useCallback(
    (selected) => handleMultiSelectChange(originFilter, selected),
    [handleMultiSelectChange],
  );

  return (
    <>
      <ModalAside
        isOpen={
          show && !isSavedSearchModalOpen && !isSavedSearchUpsellModalOpen
        }
        onDidDismiss={hideModal}
        headerContent={
          !savedSearchModalMode
            ? t('filter.filterBy')
            : savedSearchModalMode === 'create'
            ? t('filter.createSavedSearch')
            : t('filter.editSavedSearch')
        }
        footer={
          <FilterModalFooter
            showSaveSearchOption
            disabledCreateSavedSearchOption={disabledCreateSavedSearchOption}
            handleCancel={handleClear}
            handleConfirm={handleConfirm}
            openSavedSearchModal={handleOpenSavedSearchModal}
            savedSearchModalMode={savedSearchModalMode}
            isConfirmDisabled={
              (!savedSearchNameValue.length && !!savedSearchModalMode) ||
              disabledCreateSavedSearchOption
            }
          />
        }
        footerHeight="70px"
      >
        <Stack column align="stretch" spacing="loose">
          {savedSearchModalMode && (
            <SavedSearchName
              name={savedSearchNameValue}
              onNameChange={setSavedSearchNameValue}
            />
          )}
          <Box display="flex" flexDirection="column" gap="2">
            <Label>{t('filter.status')}</Label>
            <MultiSelect
              key="status-filter"
              defaultValue={defaultStatus}
              options={statusOptionsForMultiSelect}
              placeholder={t('filters.status.all')}
              onChange={handleStatusChange}
            />
          </Box>
          <DateFilter
            label={t('filter.dateRange')}
            dateFrom={values.dateFrom}
            dateTo={values.dateTo}
            onChange={handleDateRangeChange}
          />
          <Stack column align="stretch" spacing="base">
            <Box display="flex" flexDirection="column" gap="2">
              <Label>{t('filter.paymentStatus')}</Label>
              <MultiSelect
                defaultValue={defaultPaymentStatus}
                options={paymentOptions as OptionType[]}
                placeholder={t('filters.payment.all')}
                onChange={handlePaymentStatusChange}
              />
            </Box>
            <Box display="flex" flexDirection="column" gap="2">
              <Label>{t('filter.fulfillmentStatus')}</Label>
              <MultiSelect
                defaultValue={defaultFulfillmentStatus}
                options={fulfillmentStatusOptions as OptionType[]}
                placeholder={t('filters.fulfillment.all')}
                onChange={handleFulfillmentStatusChange}
              />
            </Box>
            <Box display="flex" flexDirection="column" gap="2">
              <Label>{t('filter.shippingMethod')}</Label>
              <MultiSelect
                defaultValue={defaultShippingMethod}
                options={shippingMethodOptionsForMultiSelect}
                placeholder={t('filters.shippingMethod.all')}
                onChange={handleShippingMethodChange}
              />
            </Box>
            {hasProductsFilter && (
              <ProductsFilter
                selectedProducts={selectedProductsValues}
                exactProductSet={
                  values.exactProductSet?.toLowerCase() === 'true'
                }
                onProductsFilterChange={handleProductsFilterChange}
              />
            )}
            {hasProductUnitsCountFilter && (
              <ProductUnitsCountFilter
                onChange={handleProductsCountFilterChange}
                selectedOption={selectedProductUnitsCountValue.selectedOption}
                minUnits={selectedProductUnitsCountValue.minUnits ?? ''}
                maxUnits={selectedProductUnitsCountValue.maxUnits ?? ''}
                showErrors={hasProductsCountFilterError}
              />
            )}
            {hasFilterWholesaleTag && (
              <OrdersWholesaleFilter
                value={values.isWholesale}
                onChange={handleChange}
              />
            )}
          </Stack>
          <Stack column align="stretch" spacing="base">
            <OrderPaymentMethodsFilter
              defaultValue={defaultPaymentMethod}
              paymentMethodOptions={paymentMethodOptionsForMultiSelect}
              onChange={(selected) =>
                handleMultiSelectChange(paymentMethodsFilter, selected)
              }
            />
            <OrderPaymentProvidersFilter
              defaultValue={defaultPaymentProvider}
              paymentProviderOptions={paymentProviderOptionsForMultiSelect}
              onChange={(selected) =>
                handleMultiSelectChange(paymentProviderFilter, selected)
              }
            />
            {showSubscriptionFilter && (
              <OrdersSubscriptionFilter
                value={values.isSubscription}
                onChange={handleChange}
              />
            )}
            <Box display="flex" flexDirection="column" gap="2">
              <Box display="flex" alignItems="center" gap="2">
                <Label>{t('filter.coupons.title')}</Label>
              </Box>
              <SearchMultiSelect
                options={couponOptions}
                defaultValue={defaultCouponOptions}
                placeholder={t('filter.coupons.inputPlaceholder')}
                onChange={handleCouponChange}
                onInputChange={onInputChange}
                noOptionsMessage={() => t('filter.coupons.noResults')}
                leftIcon={SearchIcon}
                loading={loading}
                minSearchLength={2}
              />
            </Box>
            <Box display="flex" flexDirection="column" gap="2">
              <Label>{t('filter.origin')}</Label>
              <MultiSelect
                defaultValue={defaultOrigin}
                options={originOptionsForMultiSelect}
                placeholder={t('filters.origin.all')}
                onChange={(selected) => handleOriginChange(selected)}
              />
            </Box>
            {isLocationsFilterShowable && (
              <Box display="flex" flexDirection="column" gap="2">
                <Label>{t('filter.location')}</Label>
                <MultiSelect
                  defaultValue={defaultLocation}
                  options={locationOptionsForMultiSelect}
                  placeholder={t('filters.location.all')}
                  onChange={handleLocationChange}
                />
              </Box>
            )}
            <OrderApplicationsFilter
              application={values.appId}
              applicationOptions={applicationOptions}
              handleChange={handleChange}
            />
          </Stack>

          <MetafieldsOrdersFilters
            selecteds={selecteds}
            onChange={onChangeMetafieldFilter}
          />
        </Stack>
      </ModalAside>

      <CreateSavedSearchModal
        open={isSavedSearchModalOpen}
        onDismiss={closeSavedSearchModal}
        onConfirm={handleConfirm}
        filters={values}
        confirmText={t('savedSearches.creationModal.save')}
      />
    </>
  );
}

export default FilterModal;
